// src/components/ExportMenu/ExportMenu.jsx
import React, { useState, useRef, useEffect } from 'react';
import { FileDown, FileText, File, X, ChevronDown, Info, AlertCircle } from 'lucide-react';
import { exportToWord, exportToPdf } from '../../utils/exportUtils';

const ExportMenu = ({ queryData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const menuRef = useRef(null);

  // Check if query is completed and can be exported
  const isCompleted = queryData && queryData.status === 'Completed';

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowInfo(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleExport = async (format) => {
    // Return early if query is not completed
    if (!isCompleted) {
      setExportError("Export is only available for completed research");
      return;
    }
    
    setIsExporting(true);
    setExportError(null);
    
    try {
      let success = false;
      
      if (format === 'word') {
        success = await exportToWord(queryData);
      } else if (format === 'pdf') {
        success = exportToPdf(queryData);
      }
      
      if (!success) {
        throw new Error(`Failed to export to ${format.toUpperCase()}`);
      }
      
      // Close menu after successful export
      setIsOpen(false);
    } catch (error) {
      console.error(`Error exporting to ${format}:`, error);
      setExportError(`Failed to export. ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const toggleInfo = (e) => {
    e.stopPropagation();
    setShowInfo(!showInfo);
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Export Button - Disabled if not completed */}
      <button
        className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
          isCompleted 
            ? "text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100"
            : "text-gray-500 bg-gray-100 border border-gray-200 cursor-not-allowed"
        }`}
        onClick={() => isCompleted && setIsOpen(!isOpen)}
        disabled={isExporting || !isCompleted}
        title={!isCompleted ? "Export is only available for completed research" : "Export research"}
      >
        <FileDown className="h-4 w-4 mr-2" />
        Export
        <ChevronDown className="h-4 w-4 ml-1" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10 animate-fade-in">
          <div className="py-1 relative">
            <div className="flex justify-between items-center px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
              <span className="font-medium">Export Options</span>
              <div className="flex">
                <button
                  className="text-gray-400 hover:text-gray-600 mr-1.5 p-1 rounded hover:bg-gray-100 transition-colors"
                  onClick={toggleInfo}
                  title="Export information"
                >
                  <Info className="h-4 w-4" />
                </button>
                <button
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            {/* Status message for non-completed research */}
            {!isCompleted && (
              <div className="px-4 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100 flex items-start">
                <AlertCircle className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 mt-0.5" />
                <span>Research must be in "Completed" status to export</span>
              </div>
            )}
            
            {/* Export Info Box */}
            {showInfo && (
              <div className="p-3 bg-blue-50 text-xs text-blue-600 border-b border-blue-100">
                <p className="mb-1 font-semibold">About Exports:</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>Exports are only available for completed research</li>
                  <li>PDF exports include formatting and are optimized for printing</li>
                  <li>Word documents can be edited after export</li>
                  <li>All exports include research results and case references</li>
                </ul>
              </div>
            )}
            
            {exportError && (
              <div className="px-4 py-2 text-xs text-red-700 bg-red-50 border-b border-red-100 flex items-start">
                <AlertCircle className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 mt-0.5" />
                <span>{exportError}</span>
              </div>
            )}
            
            <button
              className={`flex w-full items-center px-4 py-3 text-sm ${
                isCompleted 
                  ? "text-gray-700 hover:bg-gray-100"
                  : "text-gray-400 cursor-not-allowed"
              } transition-colors`}
              onClick={() => isCompleted && handleExport('pdf')}
              disabled={!isCompleted || isExporting}
            >
              <File className={`h-4 w-4 mr-3 ${isCompleted ? "text-red-600" : "text-gray-400"}`} />
              <div className="text-left">
                <span className="font-medium">Export to PDF</span>
                <span className="block text-xs text-gray-500 mt-0.5">Optimized for printing and sharing</span>
              </div>
            </button>
            
            <button
              className={`flex w-full items-center px-4 py-3 text-sm ${
                isCompleted 
                  ? "text-gray-700 hover:bg-gray-100"
                  : "text-gray-400 cursor-not-allowed"
              } transition-colors`}
              onClick={() => isCompleted && handleExport('word')}
              disabled={!isCompleted || isExporting}
            >
              <FileText className={`h-4 w-4 mr-3 ${isCompleted ? "text-blue-600" : "text-gray-400"}`} />
              <div className="text-left">
                <span className="font-medium">Export to Word (.docx)</span>
                <span className="block text-xs text-gray-500 mt-0.5">Editable document format</span>
              </div>
            </button>
            
            {isExporting && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-500 border-t-transparent mb-2"></div>
                <span className="text-xs text-gray-600 font-medium">Preparing document...</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportMenu;